'use client';

import { useEffect } from 'react';
import { Environment, PageController, WidgetsProvider } from '@sitecore-search/react';
import { usePathname } from 'next/navigation';

type WidgetsProviderProps = Parameters<typeof WidgetsProvider>[0];

/**
 * Enables Sitecore Search JS SDK widgets + OOTB tracking (widget view / click).
 * Requires NEXT_PUBLIC_SITECORE_SEARCH_* (or next.config env aliases of SITECORE_SEARCH_*).
 */
export function SitecoreSearchWidgetsProviderWrapper({ children, ...props }: WidgetsProviderProps) {
  const pathname = usePathname() ?? '/';
  const locale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en';

  const apiKey =
    process.env.NEXT_PUBLIC_SITECORE_SEARCH_API_KEY ||
    process.env.SITECORE_SEARCH_API_KEY ||
    props.apiKey;
  const customerKey =
    process.env.NEXT_PUBLIC_SITECORE_SEARCH_CUSTOMER_KEY ||
    process.env.SITECORE_SEARCH_CUSTOMER_KEY ||
    props.customerKey;
  const sitecoreSearchEnv =
    process.env.NEXT_PUBLIC_SITECORE_SEARCH_ENV ||
    process.env.SITECORE_SEARCH_ENV ||
    props.env;

  const hasCredentials = Boolean(apiKey && customerKey);

  useEffect(() => {
    if (!hasCredentials || typeof window === 'undefined') {
      return;
    }
    // Keep page URI in sync so SDK page/session context stays accurate on App Router navigations.
    PageController.getContext().setPageUri(pathname);
  }, [pathname, hasCredentials]);

  if (!hasCredentials) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        'Sitecore Search API key or Customer Key not provided — React Search SDK widgets/tracking disabled'
      );
    }
    // Avoid mounting WidgetsProvider without credentials (breaks the site).
    return <>{children}</>;
  }

  const localValues = splitLanguageAndCountry(locale);
  PageController.getContext().setLocaleLanguage(localValues.language);
  PageController.getContext().setLocaleCountry(localValues.country);
  PageController.getContext().setPageUri(pathname);

  return (
    <WidgetsProvider
      apiKey={apiKey}
      customerKey={customerKey}
      env={(sitecoreSearchEnv || 'prod') as Environment}
      publicSuffix={true}
      {...props}
    >
      {children}
    </WidgetsProvider>
  );
}

function splitLanguageAndCountry(inputValue?: string) {
  const defaultCountry = 'us';
  const value = inputValue || '';
  const [language = '', country = defaultCountry] = value.split('-');
  return {
    language: language.toLowerCase(),
    country: country.toLowerCase(),
  };
}
